/**
 * Token-based authentication utilities for PWA mode
 */
import { isPwaMode } from '@/utils/pwaUtils';
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { generateDeviceFingerprint } from '@/utils/deviceFingerprint';
import { logger } from '@/utils/logger';

// --- Storage Keys ---
const AUTH_TOKEN_KEY = 'pwa_auth_token'; // Reverted to original key

/**
 * Saves authentication token to localStorage.
 */
export const saveAuthToken = async (token: string): Promise<void> => {
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    logger.info('Authentication token saved to localStorage.');
  } catch (error) {
    logger.error('Failed to save auth token:', error);
  }
};

/**
 * Retrieves authentication token from localStorage.
 */
export const getAuthToken = async (): Promise<string | null> => {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch (error) {
    logger.error('Failed to get auth token:', error);
    return null;
  }
};

/**
 * Checks if the user is likely authenticated based on the local token.
 */
export const hasAuthStatus = (): boolean => {
  return !!localStorage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Clears authentication token and triggers logout.
 * IMPORTANT: For PWA mode, this does NOT call supabase.auth.signOut()
 * to prevent invalidating PC sessions. The server-side /logout endpoint
 * handles session invalidation with 'local' scope.
 */
export const clearAuthToken = async (): Promise<void> => {
  logger.info('[DEBUG] tokenAuthUtils.ts: clearAuthToken CALLED');

  // Get token BEFORE clearing (needed for server-side logout)
  const tokenForLogout = localStorage.getItem(AUTH_TOKEN_KEY);
  try {
    // Clear local token
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem('subscription_details');

    // Clear session storage caches
    const authRelatedKeys = [
      'auth_redirect_active',
      'auth_attempt_timestamp',
      'auth_redirect_count',
      'authCache_lastCheck',
      'authCache_isAuthenticated',
      'authCache_expiresAt',
      'auth_authenticated'
    ];

    authRelatedKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });

    // Clear Supabase SDK local state
    // IMPORTANT: For PWA mode, we DON'T call supabase.auth.signOut() because:
    // 1. It would invalidate ALL sessions (PC + PWA)
    // 2. The /logout endpoint handles session invalidation with 'local' scope
    // 3. We only want to clear local storage and invalidate THIS device's session
    if (isPwaMode()) {
      // For PWA: Clear local Supabase session WITHOUT calling signOut() API
      // This prevents invalidating PC sessions while still clearing local state
      logger.info('[DEBUG] PWA mode: Clearing local Supabase session without API call');

      if (supabase && supabase.auth) {
        // Stop auto-refresh to prevent token renewal
        await supabase.auth.stopAutoRefresh();

        // Manually clear the local session storage keys that Supabase uses
        // This prevents Welcome page from seeing an active session
        const storageKey = `sb-${SUPABASE_URL?.split('//')[1]?.split('.')[0]}-auth-token`;
        localStorage.removeItem(storageKey);

        // Also clear any potential legacy keys
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('supabase.auth.') || key.startsWith('sb-')) {
            localStorage.removeItem(key);
          }
        });

        logger.info('[DEBUG] Local Supabase session cleared');
      }
    } else {
      // For PC/web mode: Call signOut() as normal
      logger.info('[DEBUG] Web mode: Calling supabase.auth.signOut()');
      if (supabase && supabase.auth) {
        await supabase.auth.signOut();
      }
    }

    // Call server logout to invalidate PWA token and its associated sessions
    if (isPwaMode()) {
      try {
        // Note: getAuthToken() will return null after we just removed it above,
        // so we need to get it before clearing
        // Use the token we saved earlier
        if (tokenForLogout) {
          logger.info("[DEBUG] Calling logout endpoint to invalidate PWA session");
          await fetch(`${SUPABASE_URL}/functions/v1/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({ token: tokenForLogout }),
          });
        }
      } catch (backendError) {
        logger.error('[DEBUG] Error during backend logout:', backendError);
      }
    }

    logger.info('[DEBUG] Auth cleared successfully.');

    // Dispatch global event
    window.dispatchEvent(new CustomEvent('pwa-force-logout'));

  } catch (error) {
    logger.error('[DEBUG] Critical error in clearAuthToken:', error);
  }
};

/**
 * Determines if PWA token-based auth should be used.
 */
export const shouldUsePwaTokenAuth = async (): Promise<boolean> => {
  if (!isPwaMode()) return false;
  const token = await getAuthToken();
  return !!token;
};

/**
 * Validates an authentication token by calling the token-auth-validate edge function.
 */
export const validateAuthToken = async (token?: string): Promise<{ valid: boolean; userId?: string; error?: string; revoked?: boolean }> => {
  const tokenToValidate = token || await getAuthToken();

  if (!tokenToValidate) {
    return { valid: false, error: 'No token found' };
  }

  try {
    // Ensure we have the Supabase URL
    const baseUrl = SUPABASE_URL || (window.location.origin.includes('localhost')
      ? 'https://oihdhlvacxrixrsafron.supabase.co'
      : window.location.origin);

    // Call validation endpoint
    const response = await fetch(`${baseUrl}/functions/v1/token-auth-validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
      body: JSON.stringify({
        token: tokenToValidate,
        deviceFingerprint: await generateDeviceFingerprint(),
        isPwa: true, // This validation is only called from PWA contexts
        cacheBust: Date.now()
      }),
      cache: 'no-store'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      logger.error('Token validation error:', errorData);

      if (response.status === 401 || errorData.revoked) {
        logger.info('Token invalid or revoked, clearing auth');
        await clearAuthToken();
      }

      return {
        valid: false,
        error: errorData.error || `Validation failed with status: ${response.status}`,
        revoked: errorData.revoked
      };
    }

    const data = await response.json();
    logger.info('Token validation response:', data);

    if (!data.valid) {
      await clearAuthToken();
    }

    return {
      valid: !!data.valid,
      userId: data.userId,
      error: data.error,
      revoked: data.revoked
    };

  } catch (error) {
    logger.error('Token validation error:', error);
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Unknown validation error'
    };
  }
};

/**
 * Establishes a Supabase session directly from access and refresh tokens
 */
export const establishSessionFromTokens = async (
  accessToken: string,
  refreshToken?: string
): Promise<boolean> => {
  try {
    if (!accessToken) return false;

    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken || '',
    });

    if (error) {
      logger.error("Failed to establish session:", error);
      return false;
    }

    return !!data.session;
  } catch (error) {
    logger.error("Error establishing session:", error);
    return false;
  }
};

/**
 * Establishes a Supabase session from a magic link
 */
export const establishSessionFromMagicLink = async (magicLink: string): Promise<boolean> => {
  try {
    // For the exchange part:
    const exchangeResponse = await fetch(`${SUPABASE_URL}/functions/v1/token-auth-exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`
      },
      body: JSON.stringify({ magicLink }),
    });

    if (exchangeResponse.ok) {
      const exchangeData = await exchangeResponse.json();
      if (exchangeData.authResponse?.access_token) {
        return await establishSessionFromTokens(
          exchangeData.authResponse.access_token,
          exchangeData.authResponse.refresh_token
        );
      }
    }
    return false;
  } catch (error) {
    logger.error("Error exchanging magic link:", error);
    return false;
  }
};

export const isTokenAuthAvailable = (): boolean => {
  return isPwaMode();
};
